import { Link, NavigateOptions, Outlet, To, useBeforeUnload } from "react-router-dom";
import { useAuth, useUser } from "../../../../utils/auth";
import { useNavigate } from "react-router-dom";
import { Col, Container, Row, Dropdown } from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';
import Menu from '../components/Menu';
import BreadcrumbAdmin from '../components/BreadcrumbAdmin';
import { updateLanguageAPI } from "../../../../features/auth/api/authAPI";
import i18n from '../../../../i18n';
import { useTranslation } from "react-i18next";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { NavigationGuardConfig, NavigationGuardContext } from "../../../../contexts/NavigationGuardContext";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import { useState, useCallback } from "react";
import { getClientParamsSchema } from "../../generalParams/schema/clientParamsSchema";
import { useDispatch, useSelector } from "react-redux";
import { updateGeneralParams } from "../../generalParams/api/generalParamsAPI";
import ClientPreferencesForm from "../../../../components/Layouts/preferences/ClientPreferencesForm";
import { clearErrors, setError } from "../../../../redux/surveySlice";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { RootState } from "../../../../redux/store";

export default function Admin() {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const userInfo = useUser(); 
    const { showModal, openModal, closeModal } = useModal();
    const [preferencesMessage, setPreferencesMessage] = useState("");
    const [preferencesMessageKey, setPreferencesMessageKey] = useState(0);
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);
    const dispatch = useDispatch();
    const clientParamsForm = useSelector((state: RootState) => state.clientParamsForm.data);

    const [navigationGuard, setNavigationGuard] = useState<NavigationGuardConfig>({
        enabled: false,
        message: "",
        messageButtonSave: "",
        messageButtonClose: ""
    });

    const [pendingNavigation, setPendingNavigation] = useState<{
        to: To;
        options?: NavigateOptions;
    } | null>(null);

    const navigateWithGuard = useCallback((to: To, options?: NavigateOptions) => {
        if (navigationGuard.enabled) {
            setPendingNavigation({ to, options });
            if (!showModal) {
                openModal(null, "", "", "info", "");
            }
            return;
        }
        navigate(to, options);
    }, [navigate, navigationGuard.enabled, openModal, showModal]);


    useBeforeUnload(
        useCallback((event) => {
            if (!navigationGuard.enabled) return;
            event.preventDefault();
            event.returnValue = "";
        }, [navigationGuard.enabled])
    );

    const handleCancelNavigation = () => {
        closeModal();
        if (pendingNavigation) {
            navigate(pendingNavigation.to,pendingNavigation.options);
            setPendingNavigation(null);
        }
        setNavigationGuard({
            enabled: false,
            message: "",
            messageButtonSave: "",
            messageButtonClose: "",
        });
    };

    const handleSaveNavigation = async () => {
        closeModal();
        let canNavigation = true;
        if (navigationGuard.onSave) {

            canNavigation = await navigationGuard.onSave(
                typeof pendingNavigation?.to === "string"
                    ? pendingNavigation.to
                    : pendingNavigation?.to.pathname,
                pendingNavigation?.options?.state
            );
        }

        if (pendingNavigation && canNavigation) {

            navigate(
                pendingNavigation.to,
                pendingNavigation.options
            );

            setPendingNavigation(null);
        }

        setNavigationGuard({
            enabled: false,
            message: "",
            messageButtonSave: "",
            messageButtonClose: "",
        });
    };

    const handleLogout = () => {
        logout();
    };

    const handleDasboard = () => {
        navigateWithGuard("/");
    };

    const changeLanguage = async (language:string) => {
        await updateLanguageAPI(language);
        i18n.changeLanguage(language);
    };
    const { t } = useTranslation('common');
  
    const showModalPreferences = () => {
        setPreferencesMessage("");
        setShowPreferencesModal(true);
    };

    const closeModalPreferences = () => {
        setPreferencesMessage("");
        setShowPreferencesModal(false);
    };

     const onSubmit = async () => {
            const schema = getClientParamsSchema(t);
            dispatch(clearErrors());
    
            try {
                await schema.validate(clientParamsForm, { abortEarly: false });
                
                await updateGeneralParams(clientParamsForm);
                setPreferencesMessageKey((prev) => prev + 1);
                setPreferencesMessage(t("clientParams:saveUserPreferencesSuccessfully"));
    
            } catch (err: any) {
                if (Array.isArray(err?.inner) && err.inner.length > 0) {
                    err.inner.forEach((error: any) => {
                        dispatch(setError({ 
                            field: error.path,
                            message: error.message 
                        }));
                    });
                    return;
                }
    
                const apiError = err as ApiErrorResponse;
                setPreferencesMessage(apiError?.message || t("common:error"));
            }
        }
        
    return (
        <>
            <ModalGlobal
                show={showModal}
                onCancel={handleCancelNavigation}
                onClose={closeModal}
                body={navigationGuard.message}
                onSave={handleSaveNavigation}
                saveText={navigationGuard.messageButtonSave}
                closeText={navigationGuard.messageButtonClose}
                isLoading={false}
            />
            <ModalGlobal
                show={showPreferencesModal}
                onClose={closeModalPreferences}
                onSave={onSubmit}
                title={t("clientParams:preferences.modalPreferencesTitle")}
                body={<ClientPreferencesForm messageName={preferencesMessage} messageKey={preferencesMessageKey}/>}
                closeText={t("common:buttons.buttonCancel")}
                isLoading={false}
                size="lg"
                saveText={t("common:buttons.buttonSave")}
                dialogClassName="preferences-modal"
            />
            <NavigationGuardContext.Provider value={{ setNavigationGuard, navigateWithGuard }}>
                <Container fluid className="bg-light p-0">
                    <div className="navbar navbar-light bg-light shadow-sm">
                        <div className="container-fluid">
                            <Link to="#"  onClick={(e) => {
                                e.preventDefault();
                                navigateWithGuard("/claims/dashboard");
                                }}
                                className="navbar-brand d-flex align-items-center"
                                style={{ textDecoration: 'none', color: 'black' }}>
                                <img
                                    src={userInfo.userInfo?.clientLogo
                                        ? `data:${userInfo.userInfo.clientLogoContentType};base64,${userInfo.userInfo.clientLogo}` 
                                        : "/logo-craiteria.svg"
                                    }
                                    alt="Craiteria"
                                    style={{ width: '200px', }}
                                />
                            </Link>
                            <Row>
                                <Col>
                                    <OverlayTrigger
                                        placement="left"
                                        overlay={
                                            <Tooltip id="tooltip-info">
                                                {t("common:buttons.buttonGoToDashboard")}
                                            </Tooltip>
                                        }
                                    >
                                        <button onClick={handleDasboard} className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarHeader" aria-controls="navbarHeader" aria-expanded="false" aria-label="Toggle navigation">
                                            <img
                                                src="/ico-index.png"
                                                alt="Index"
                                                style={{ width: '50px' }}
                                            />
                                        </button>
                                    </OverlayTrigger>
                                </Col>
                                <Col>
                                    <Dropdown>
                                        <Dropdown.Toggle as="div" id="custom-dropdown" className="navbar-toggler" style={{ cursor: 'pointer' }}>
                                            <img
                                                src="/ico-user.png"
                                                alt="User"
                                                style={{ width: '50px', }}
                                            />
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="dropdown-menu-text-black" style={{ minWidth: "auto", width: "190px", padding: "0" }}>
                                            <Dropdown drop="end">
                                                <Dropdown.Toggle as="div" className="dropdown-item" style={{ cursor: "pointer", padding: "0.25rem 1rem" }}>
                                                    {t("auth:languages")}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu style={{ minWidth: "auto", width: "100px" }}>
                                                    <Dropdown.Item onClick={async () => await changeLanguage("ca")} style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>CA</Dropdown.Item>
                                                    <Dropdown.Item onClick={async () => await changeLanguage("en")} style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>EN</Dropdown.Item>
                                                    <Dropdown.Item onClick={async () => await changeLanguage("es")} style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>ES</Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                            <Dropdown.Item onClick={showModalPreferences} style={{ whiteSpace: "normal", overflow: "visible" }}>{t("auth:buttonModalPreferences")}</Dropdown.Item>
                                            <Dropdown.Item onClick={handleLogout} style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>{t("auth:buttonLogOut")}</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Container>
                <Container fluid>
                    <Row>
                        <Col md={2} className="pl-0">
                            <Menu />
                        </Col>
                        <Col md={10} className="ps-3 pe-4 pt-3">
                            <BreadcrumbAdmin />
                            <Outlet />
                        </Col>
                    </Row>
                </Container>
                <Container fluid>
                    <Row className="justify-content-between py-2" id="footer">
                        <div className="container">
                            <Col>
                               <p>� 2024 - CraiterIA  - <a href="/Claims/TermsOfService">Terms of Service</a></p>
                            </Col>
                        </div>
                    </Row>
                </Container>
            </NavigationGuardContext.Provider>
        </>
    );
}
